/* eslint complexity: ["error", 21] */
import { ActivityMiddleware } from 'botframework-webchat-api';
import { createActivityPolymiddlewareFromLegacy, type Polymiddleware } from 'botframework-webchat-api/middleware.js';
import {
  getActivityLivestreamingMetadata,
  getOrgSchemaMessage,
  isVoiceTranscriptActivity,
  type WebChatActivity
} from 'botframework-webchat-core';
import { type OrgSchemaCreativeWork } from 'botframework-webchat-core/org-schema.js';
import React from 'react';

import CarouselLayout from '../../Activity/CarouselLayout';
import StackedLayout from '../../Activity/StackedLayout';

// TODO: [P4] Can we simplify these if-statement to something more readable?
function shouldFilterActivity(activity: WebChatActivity, messageThing: OrgSchemaCreativeWork): boolean {
  const { type } = activity;
  if (
    type === 'conversationUpdate' ||
    (type === 'event' && !isVoiceTranscriptActivity(activity)) ||
    type === 'invoke' ||
    // Do not show content for contentless livestream interims, or finalized activity without content.
    (type === 'typing' &&
      (getActivityLivestreamingMetadata(activity)?.type === 'contentless' ||
        !(activity['text'] || activity.attachments?.length > 0 || messageThing?.abstract.length))) ||
    (type === 'message' &&
      // Do not show postback
      (activity.channelData?.postBack ||
        // Do not show messageBack if displayText is undefined
        (activity.channelData?.messageBack && !activity.channelData.messageBack.displayText) ||
        // Do not show empty bubbles (no text and attachments)
        !(activity.text || activity.attachments?.length || messageThing?.abstract.length)))
  ) {
    return true;
  }

  return false;
}

/**
 * @deprecated Use `defaultActivityPolymiddleware` instead. The `createCoreActivityMiddleware` will be removed on or after 2028-03-16.
 */
function createCoreActivityMiddleware(): readonly ActivityMiddleware[] {
  return Object.freeze([
    () =>
      next =>
      (...args) => {
        const [{ activity }] = args;
        const isMessageOrTyping = activity.type === 'message' || activity.type === 'typing';

        const messageThing = getOrgSchemaMessage(activity.entities);

        // Filter out activities that should not visible.
        if (shouldFilterActivity(activity, messageThing)) {
          return false;
        } else if (isMessageOrTyping || isVoiceTranscriptActivity(activity)) {
          if (
            isMessageOrTyping &&
            (activity.attachments?.length || 0) > 1 &&
            activity.attachmentLayout === 'carousel'
          ) {
            // The following line is not a React functional component, it's a render function called by useCreateActivityRenderer() hook.
            // The function signature need to be compatible with older version of activity middleware, which was:
            //
            // renderActivity(
            //   renderAttachment: ({ activity, attachment }) => React.Element
            // ) => React.Element

            return function renderCarouselLayout(renderAttachment, props) {
              typeof props === 'undefined' &&
                console.warn(
                  'botframework-webchat: One or more arguments were missing after passing through the activity middleware. Please check your custom activity middleware to make sure it passes all arguments.'
                );

              return <CarouselLayout activity={activity} renderAttachment={renderAttachment} {...props} />;
            };
          }

          // The following line is not a React functional component, it's a render function called by useCreateActivityRenderer() hook.
          return function renderStackedLayout(renderAttachment, props) {
            typeof props === 'undefined' &&
              console.warn(
                'botframework-webchat: One or more arguments were missing after passing through the activity middleware. Please check your custom activity middleware to make sure it passes all arguments.'
              );

            return <StackedLayout activity={activity} renderAttachment={renderAttachment} {...props} />;
          };
        }

        return next(...args);
      }
  ]);
}

const defaultActivityPolymiddleware: Polymiddleware = createActivityPolymiddlewareFromLegacy(
  ...createCoreActivityMiddleware()
);

export default defaultActivityPolymiddleware;
export {
  // Exporting `createCoreActivityMiddleware()` for backward compatibility.
  createCoreActivityMiddleware
};
